import type { TextRenderSnapshot } from '@/framework/services/text-render';
import { isPositiveFiniteNumber } from '@/utils/math-kit';
import { type ILayerProps, type ITextLayerProps } from '@/version-types';

/**
 * 根据排版快照同步文本图层的内容尺寸。
 *
 * 列表缩进计入对应的内容轴；固定行内尺寸时，横排保留图层宽度、竖排保留图层高度，
 * 仅使用快照更新另一轴。自适应行内尺寸为零时收缩至 1px，其他无效尺寸保留图层原尺寸。
 *
 * @param layer - 待更新尺寸的文本图层。
 * @param snapshot - 当前图层的排版快照。
 * @returns 写入有效排版尺寸后的新图层。
 */
export const applySnapshotSize = (layer: ILayerProps, snapshot: TextRenderSnapshot): ILayerProps => {
    const isHorizontal = snapshot.writingMode === 'horizontal-tb';
    const fixedInlineSize = layer.__fixedWidth !== false;
    const snapshotWidth = snapshot.contentWidth + (isHorizontal ? snapshot.listMarkerIndent : 0);
    const snapshotHeight = snapshot.contentHeight + (isHorizontal ? 0 : snapshot.listMarkerIndent);
    // 全选默认文字后回车，纯换行的自然行内尺寸是 0，这仍是合法的排版结果。
    // 旧逻辑把 0 当成无效尺寸并保留旧宽度，造成自适应选框不收缩、空行光标却靠左的错位。
    // 自适应行内轴为 0 时使用与引擎二次排版一致的 1px 最小尺寸，不能沿用旧框或强制固定排版。
    // 只处理合法的零尺寸；非有限值、负值仍走下面的保护逻辑，固定文字框也继续保留用户约束。
    const width =
        fixedInlineSize && isHorizontal
            ? layer.width
            : !fixedInlineSize && isHorizontal && snapshotWidth === 0
              ? 1
              : snapshotWidth;
    const height =
        fixedInlineSize && !isHorizontal
            ? layer.height
            : !fixedInlineSize && !isHorizontal && snapshotHeight === 0
              ? 1
              : snapshotHeight;
    const nextWidth = isPositiveFiniteNumber(width) ? width : layer.width;
    const nextHeight = isPositiveFiniteNumber(height) ? height : layer.height;

    return {
        ...layer,
        width: nextWidth,
        height: nextHeight,
    };
};

/**
 * 为正在编辑的空 Text Layer 创建宿主专属的 caret 几何。
 *
 * Text Render 的 empty 状态不发布 snapshot；只有 Text Layer 编辑宿主需要使用
 * 当前行内尺寸和单行尺寸补出空几何，因此该兼容逻辑必须留在 renderer contract 之外。
 *
 * @param layer - 当前 Text Layer 编辑工作值。
 * @returns 空编辑态返回最小可用 snapshot，其他状态返回 null。
 */
export const createEmptyEditingSnapshot = (layer: ILayerProps): TextRenderSnapshot | null => {
    const data = layer.data as ITextLayerProps;
    if (!layer.__isOnTextEditing || data.text !== '') return null;

    // 删除最后一个换行后，Renderer 返回 empty，由这里补出编辑几何；此时 layer 的宽高仍是
    // 删除前两行（竖排两列）的尺寸。若直接复用宽高，applySnapshotSize 就会把旧尺寸重新写回，
    // 导致内容已空、光标已回到第一行，但选框仍有两行高。直接全选删除多行文字也会触发此问题。
    // 空文本只保留一行：沿行排列方向恢复字号 × 行距的尺寸，与编辑器兜底光标大小一致；
    // 行内方向仅在固定尺寸时保留原约束；自适应文字清空后也应收缩到 1px，不能保留删除前的宽度。
    const isHorizontal = data.writingMode === 'horizontal-tb';
    const lineSize = Math.max(Math.ceil(data.fontSize * (data.lineSpacing || 1.2)), 1);
    const previousInlineSize = isHorizontal ? layer.width : layer.height;
    const inlineSize =
        layer.__fixedWidth !== false && isPositiveFiniteNumber(previousInlineSize) ? previousInlineSize : 1;
    const width = isHorizontal ? inlineSize : lineSize;
    const height = isHorizontal ? lineSize : inlineSize;

    return {
        writingMode: data.writingMode as TextRenderSnapshot['writingMode'],
        instructions: [],
        glyphInfos: [],
        lineMetrics: [],
        spanRanges: [],
        listMarkerIndent: 0,
        contentWidth: width,
        contentHeight: height,
        minInlineSize: isHorizontal ? width : height,
    };
};
